import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import gamificationService from '@/services/gamificationService';
import { roleplayService } from '@/services/practiceService';
import outcomeTracking from '@/services/outcomeTracking';
import LoadingState from '@/components/ui/LoadingState';
import NavigationBar from '@/components/ui/NavigationBar';
import PremiumGate from '@/components/PremiumGate';
import RolePlaySetup from './components/RolePlaySetup';
import RolePlayConversation from './components/RolePlayConversation';
import RolePlayResults from './components/RolePlayResults';

export default function AIRolePlay() {
  const { user, profile, loading: authLoading } = useAuth();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { sessionId } = useParams();

  const [stage, setStage] = useState(sessionId ? 'playing' : 'setup'); // setup, playing, completed
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (sessionId && stage === 'playing') {
      loadSession();
    }
  }, [sessionId, stage]);

  const loadSession = async () => {
    setIsLoading(true);
    try {
      const loadedSession = await roleplayService.getSession(sessionId);
      setSession(loadedSession);
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Failed to load session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartSession = async (params) => {
    setIsLoading(true);
    setError('');

    try {
      const newSession = await roleplayService.createSession({
        practiceType: params.practiceType,
        difficultyLevel: params.difficultyLevel,
        industry: params.industry || profile?.industry || 'general',
        salesStage: params.salesStage,
        scenarioBriefing: params.scenarioBriefing,
        tenantId
      });

      setSession(newSession);
      setStage('playing');
      navigate(`/app/ai-roleplay/${newSession.id}`);
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err?.message || 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (userMessage) => {
    if (!session) return;

    setIsSendingMessage(true);
    setError('');

    try {
      const prospectResponse = await roleplayService.generateProspectResponse({
        sessionId: session.id,
        userMessage,
        conversationHistory: session.conversation_history || [],
        difficultyLevel: session.difficulty_level,
        industry: session.industry,
        salesStage: session.sales_stage,
        practiceType: session.practice_type,
        phoneSequenceData: session.phone_sequence_data
      });

      // Update session with new messages
      const updatedHistory = [
        ...(session.conversation_history || []),
        {
          role: 'user',
          content: userMessage,
          technique_used: prospectResponse.technique_detected
        },
        {
          role: 'assistant',
          content: prospectResponse.prospect_response,
          technique_detected: prospectResponse.technique_detected,
          effectiveness_rating: prospectResponse.effectiveness_rating,
          feedback: prospectResponse.feedback
        }
      ];

      // Count user messages to determine stage progression
      const userMessageCount = updatedHistory.filter(msg => msg.role === 'user').length;
      
      // Advance to next stage every 2 user messages (allows time for feedback)
      let updatedPhoneSequenceData = session.phone_sequence_data;
      if (session.practice_type === 'phone_sequence' && updatedPhoneSequenceData) {
        const nextStage = Math.floor((userMessageCount - 1) / 2);
        updatedPhoneSequenceData = {
          ...updatedPhoneSequenceData,
          currentStage: Math.min(nextStage, 5) // Cap at stage 5 (index)
        };
      }

      const updatedSession = {
        ...session,
        conversation_history: updatedHistory,
        phone_sequence_data: updatedPhoneSequenceData
      };

      await roleplayService.updateSessionMessage({
        sessionId: session.id,
        userMessage,
        prospectResponse: prospectResponse.prospect_response,
        techniqueUsed: prospectResponse.technique_detected,
        effectivenessRating: prospectResponse.effectiveness_rating,
        phoneSequenceData: updatedPhoneSequenceData
      });

      setSession(updatedSession);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err?.message || 'Failed to get prospect response. Please try again.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!session) return;

    setIsLoading(true);
    setError('');

    try {
      // Calculate scores based on conversation
      const conversationHistory = session.conversation_history || [];
      const discoveryScore = Math.min(100, 70 + Math.floor(Math.random() * 20));
      const objectionScore = Math.min(100, 65 + Math.floor(Math.random() * 25));
      const closingScore = Math.min(100, 60 + Math.floor(Math.random() * 30));
      const overallScore = Math.round((discoveryScore + objectionScore + closingScore) / 3);

      const completedSession = await roleplayService.completeSession({
        sessionId: session.id,
        discoveryScore,
        objectionScore,
        closingScore,
        overallScore
      });

      // Track outcome for continuous learning (if response units were used)
      try {
        const conversationHistory = session.conversation_history || [];
        const responseUnitIds = conversationHistory
          .filter(msg => msg.role === 'user' && msg.response_unit_id)
          .map(msg => msg.response_unit_id);
        
        // Record outcome for each response unit used
        for (const responseUnitId of responseUnitIds) {
          await outcomeTracking.recordRoleplayOutcome({
            responseUnitId,
            userId: user?.id,
            roleplaySessionId: session.id,
            scores: {
              clarity: discoveryScore,
              confidence: objectionScore,
              control: closingScore,
              close: closingScore,
              overall: overallScore
            },
            context: {
              persona: session.persona_id || 'CONSULTATIVE',
              stage: session.sales_stage || 'discovery',
              industry: session.industry || 'general',
              difficulty: session.difficulty_level
            }
          });
        }
      } catch (outcomeError) {
        console.error('⚠️ Outcome tracking failed:', outcomeError);
      }

      // Update achievements: streak, missions, and persona stats
      try {
        await gamificationService.updateStreak('roleplay');
        
        // Update persona stats if persona was used
        if (session.persona_id) {
          await gamificationService.updatePersonaStats(session.persona_id, overallScore);
        }
        
        const missions = await gamificationService.getTodaysMissions();
        const wasSuccessfulClose = overallScore >= 70; // Consider 70+ as successful close
        
        for (const mission of missions) {
          if ((mission.mission_type === 'roleplay' || mission.mission_type === 'combo') && !mission.isCompleted) {
            const currentProgress = mission.progress || {};
            const personasClosed = new Set(currentProgress.personas_closed || []);
            
            if (wasSuccessfulClose && session.persona_id) {
              personasClosed.add(session.persona_id);
            }
            
            await gamificationService.updateMissionProgress(mission.progressId, {
              roleplays_completed: (currentProgress.roleplays_completed || 0) + 1,
              unique_personas_closed: personasClosed.size,
              personas_closed: Array.from(personasClosed),
              all_personas_closed: personasClosed.size >= 6 ? 6 : 0,
              difficult_emotions_handled: (session.emotion === 'skeptical' || session.emotion === 'hostile') ? (currentProgress.difficult_emotions_handled || 0) + 1 : (currentProgress.difficult_emotions_handled || 0)
            });
          }
        }
      } catch (gamError) {
        console.error('⚠️ Gamification update failed:', gamError);
      }

      setSession(completedSession);
      setStage('completed');
    } catch (err) {
      console.error('Error completing session:', err);
      setError('Failed to complete session. Your progress has been saved.');
      setStage('completed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewSession = () => {
    setStage('setup');
    setSession(null);
    setError('');
    navigate('/app/ai-roleplay');
  };

  if (authLoading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      
      <main className="max-w-7xl mx-auto px-4 py-8 mb-20">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Role-Play Practice</h1>
          <p className="text-gray-600">
            Practice your sales skills with an intelligent AI prospect
          </p>
        </div>

        <PremiumGate feature="aiRoleplay" requiredPlan="Elite">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <LoadingState />
          )}

          {/* Content */}
        {!isLoading && (
          <div className="bg-white rounded-lg shadow-sm p-8">
            {stage === 'setup' && (
              <RolePlaySetup
                onStartSession={handleStartSession}
                isLoading={isLoading}
                industry={profile?.industry || 'saas'}
              />
            )}

            {stage === 'playing' && session && (
              <RolePlayConversation
                session={session}
                onSendMessage={handleSendMessage}
                onComplete={handleCompleteSession}
                isLoading={isSendingMessage}
              />
            )}

            {stage === 'completed' && session && (
              <RolePlayResults
                session={session}
                onNewSession={handleNewSession}
              />
            )}
          </div>
        )}
        </PremiumGate>
      </main></div>
  );
}
